import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { ApiPathDataCategorization } from '../../../common/api-path/ApiPath';
import { getAccessToken } from '../../../common/Utils/msalUtils';

// Configure axios instance with default headers for data categorization service
const instance = axios.create({
  baseURL: `${ApiPathDataCategorization.baseUrl}/data-categorization`,
  headers: {
    'Content-Type': 'application/json',
    'X-User-Id': process.env.REACT_APP_USER_ID || 'SYSTEM',
  },
});

// Request interceptor for authorization (same as global AxiosInterceptor)
instance.interceptors.request.use(async (req) => {
  const token = await getAccessToken();
  req.headers['Authorization'] = token ? `Bearer ${token}` : '';
  req.headers['X-correlation-id'] = uuidv4();
  // For FormData (multipart), remove Content-Type so browser/axios sets it with the correct boundary
  if (req.data instanceof FormData) {
    delete req.headers['Content-Type'];
  }
  return req;
});

// Response interceptor to extract data from wrapper and handle 401 errors
instance.interceptors.response.use(
  (response) => {
    // Backend wraps response in {success, message, data, timestamp}
    if (response.data?.success) {
      return { ...response, data: response.data.data, message: response.data.message };
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 errors (token expiration) by retrying with fresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Get fresh token
        const token = await getAccessToken();
        originalRequest.headers['Authorization'] = token ? `Bearer ${token}` : '';
        
        // Retry the original request with new token
        return instance(originalRequest);
      } catch (err) {
        // If token refresh fails, redirect to login
        return Promise.reject(err);
      }
    }
    
    // Extract error message from backend error response
    const message = error.response?.data?.message || error.response?.data?.details || error.message;
    return Promise.reject({ ...error, message });
  }
);

// Create a separate axios instance for root API calls (teams endpoint is at root level, not under /data-categorization)
const rootInstance = axios.create({
  baseURL: ApiPathDataCategorization.baseUrl,
  headers: {
    'Content-Type': 'application/json',
    'X-User-Id': process.env.REACT_APP_USER_ID || 'SYSTEM',
  },
});

// Request interceptor for root instance
rootInstance.interceptors.request.use(async (req) => {
  const token = await getAccessToken();
  req.headers['Authorization'] = token ? `Bearer ${token}` : '';
  req.headers['X-correlation-id'] = uuidv4();
  if (req.data instanceof FormData) {
    delete req.headers['Content-Type'];
  }
  return req;
});

// Response interceptor for root instance
rootInstance.interceptors.response.use(
  (response) => {
    // Backend wraps response in {success, message, data, timestamp}
    if (response.data?.success) {
      return { ...response, data: response.data.data, message: response.data.message };
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 errors (token expiration) by retrying with fresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Get fresh token
        const token = await getAccessToken();
        originalRequest.headers['Authorization'] = token ? `Bearer ${token}` : '';
        
        // Retry the original request with new token
        return rootInstance(originalRequest);
      } catch (err) {
        // If token refresh fails, redirect to login
        return Promise.reject(err);
      }
    }
    
    // Extract error message from backend error response
    const message = error.response?.data?.message || error.response?.data?.details || error.message;
    return Promise.reject({ ...error, message });
  }
);

/**
 * Service to handle Data Categorization API calls with eiot-catAi Spring Boot Backend
 * Backend API: http://localhost:8080/api/v1
 */

/**
 * Get all incident categories with pagination
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @param {string} sortBy - Sort field
 * @param {string} sortOrder - Sort order (ASC|DESC)
 * @returns {Promise} Promise containing array of categories
 */
export const getAllIncidentCategories = (page = 0, size = 10, sortBy = 'createdAt', sortOrder = 'DESC') => {
  return instance.get('/categories', {
    params: {
      page,
      size,
      sortBy,
      sortOrder,
    },
  });
};

/**
 * Get incident category by ID
 * @param {number} categoryId - ID of the category
 * @returns {Promise} Promise containing category details
 */
export const getIncidentCategoryById = (categoryId) => {
  return instance.get(`/categories/${categoryId}`);
};

/**
 * Create a new incident category
 * @param {object} categoryData - Category data {name, description}
 * @returns {Promise} Promise containing created category (201 status)
 */
export const createIncidentCategory = (categoryData) => {
  return instance.post('/categories', categoryData);
};

/**
 * Update incident category
 * @param {number} categoryId - ID of the category to update
 * @param {object} categoryData - Updated category data {name, description, type, priority}
 * @returns {Promise} Promise containing updated category
 */
export const updateIncidentCategory = (categoryId, categoryData) => {
  // Backend uses POST for updates
  return instance.post(`/categories/${categoryId}`, categoryData);
};

/**
 * Delete incident category
 * @param {number} categoryId - ID of the category to delete
 * @returns {Promise} Promise containing deletion response
 */
export const deleteIncidentCategory = (categoryId) => {
  return instance.delete(`/categories/${categoryId}`);
};

/**
 * Change category status (ACTIVE | INACTIVE)
 * @param {number} categoryId - ID of the category
 * @param {string} status - New status (ACTIVE|INACTIVE)
 * @returns {Promise} Promise containing updated category
 */
export const changeCategoryStatus = (categoryId, status) => {
  return instance.post(`/categories/${categoryId}/status`, {
    status,
  });
};

/**
 * Bulk delete categories
 * @param {array} categoryIds - Array of category IDs to delete
 * @returns {Promise} Promise containing bulk delete response
 */
export const bulkDeleteCategories = (categoryIds) => {
  return instance.post('/categories/bulk-delete', {
    ids: categoryIds,
  });
};

/**
 * Search categories with filters
 * @param {object} searchParams - {name, description, status, createdAfter, createdBefore, page, size}
 * @returns {Promise} Promise containing search results with pagination
 */
export const searchCategories = (searchParams = {}) => {
  const {
    name = '',
    description = '',
    status = '',
    createdAfter = '',
    createdBefore = '',
    page = 0,
    size = 10,
  } = searchParams;

  return instance.get('/categories/search', {
    params: {
      name,
      description,
      status,
      createdAfter,
      createdBefore,
      page,
      size,
    },
  });
};

/**
 * Get audit log for a category
 * @param {number} categoryId - ID of the category
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise} Promise containing audit log entries
 */
export const getCategoryAuditLog = (categoryId, page = 0, size = 10) => {
  return instance.get(`/categories/${categoryId}/audit-log`, {
    params: {
      page,
      size,
    },
  });
};

/**
 * Validate category data before creation/update
 * @param {object} categoryData - Category data to validate {name, description}
 * @returns {Promise} Promise containing validation result
 */
export const validateCategoryData = (categoryData) => {
  return instance.post('/categories/validate', categoryData);
};

// ==================== RULES MANAGEMENT ====================

/**
 * Get all rules with filters and pagination
 * @param {object} params - {team, enabled, search, page, size, sort}
 * @returns {Promise} Promise containing rules array
 */
export const getAllRules = (params = {}) => {
  const {
    team = '',
    enabled,
    search = '',
    page = 0,
    size = 20,
    sort = 'createdAt,desc'
  } = params;

  // Build query params object, only including filters that are set
  const queryParams = {
    page,
    size,
    sort
  };
  
  // Only include team filter if provided
  if (team) {
    queryParams.team = team;
  }
  
  // Only include search filter if provided
  if (search) {
    queryParams.search = search;
  }
  
  // Only include enabled filter if explicitly set (true or false)
  // null or undefined means "All Status", so don't include it
  if (enabled === true || enabled === false) {
    queryParams.enabled = enabled;
  }

  return instance.get('/rules', {
    params: queryParams
  });
};

/**
 * Get rule by ID
 * @param {string} ruleId - Rule ID
 * @returns {Promise} Promise containing rule details with conditions
 */
export const getRuleById = (ruleId) => {
  return instance.get(`/rules/${ruleId}`);
};

/**
 * Create new rule with conditions
 * @param {object} ruleData - {name, team, targetCategory, enabled, logicalOperator, conditions[]}
 * @returns {Promise} Promise containing created rule
 */
export const createRule = (ruleData) => {
  return instance.post('/rules', ruleData);
};

/**
 * Update existing rule
 * @param {string} ruleId - Rule ID
 * @param {object} ruleData - Updated rule data
 * @returns {Promise} Promise containing updated rule
 */
export const updateRule = (ruleId, ruleData) => {
  return instance.post(`/rules/${ruleId}/update`, ruleData);
};

/**
 * Delete rule
 * @param {string} ruleId - Rule ID
 * @returns {Promise} Promise containing deletion response
 */
export const deleteRule = (ruleId) => {
  return instance.delete(`/rules/${ruleId}`);
};

/**
 * Toggle rule status (enable/disable)
 * @param {string} ruleId - Rule ID
 * @param {boolean} enabled - Enable or disable
 * @returns {Promise} Promise containing updated rule
 */
export const toggleRuleStatus = (ruleId, enabled) => {
  return instance.post(`/rules/${ruleId}/toggle`, { enabled });
};

// ==================== BATCH PROCESSING ====================

/**
 * Upload file for batch processing
 * @param {File} file - CSV/Excel file
 * @param {string} team - Team name (see TEAMS constant in constants.js)
 * @param {boolean} saveResults - Whether to save results to database
 * @param {Function} onUploadProgress - Callback for upload progress (percentage)
 * @returns {Promise} Promise containing batch processing response with batchId
 */
export const uploadBatchFile = (file, team, saveResults = true, onUploadProgress = null, selectedRuleIds = []) => {
  console.log('[Service] uploadBatchFile called:', {
    fileName: file?.name, 
    fileSize: file?.size,
    fileType: file?.type,
    team, 
    saveResults,
    selectedRuleIds
  });
  
  const formData = new FormData();
  formData.append('file', file);
  formData.append('team', team);
  formData.append('uploadedBy', 'SYSTEM'); // required by backend
  formData.append('saveResults', saveResults);

  // Send selected rule IDs if any are specified
  if (selectedRuleIds && selectedRuleIds.length > 0) {
    formData.append('selectedRuleIds', selectedRuleIds.join(','));
  }

  console.log('[Service] Sending POST request to /processing/batch...');
  
  return instance.post('/processing/batch', formData, {
    headers: {
      // Do NOT set Content-Type manually for FormData - let the browser/axios set it with the correct boundary
    },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.lengthComputable && onUploadProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`[Service] Upload progress: ${percentCompleted}%`);
        onUploadProgress(percentCompleted);
      }
    },
    timeout: 300000, // 5 minutes
  }).then(response => {
    console.log('[Service] Upload successful:', response.data);
    return response;
  }).catch(error => {
    console.error('[Service] Upload failed:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  });
};

/**
 * Get batch processing status
 * @param {string} batchId - Batch ID
 * @returns {Promise} Promise containing batch status and statistics
 */
export const getBatchStatus = (batchId) => {
  return instance.get(`/processing/batch/${batchId}`);
};

/**
 * Cancel batch processing
 * @param {string} batchId - Batch ID
 * @returns {Promise} Promise containing cancellation response
 */
export const cancelBatch = (batchId) => {
  return instance.post(`/processing/batch/${batchId}/cancel`);
};

/**
 * Get batch results (paginated, sortable, filterable)
 * @param {string} batchId - Batch ID
 * @param {object} params - {page, size, status, sortField, sortOrder, filters}
 * @returns {Promise} Promise containing processing results
 */
export const getBatchResults = (batchId, params = {}) => {
  const {
    page = 0,
    size = 50,
    status = '',
    sortField = '',
    sortOrder = 1,
    filters = []
  } = params;

  const queryParams = { page, size, status };

  // Add sorting if specified
  if (sortField) {
    queryParams.sortField = sortField;
    queryParams.sortOrder = sortOrder; // 1 for ASC, -1 for DESC
  }

  // Add filters (max 2)
  if (Array.isArray(filters) && filters.length > 0) {
    filters.slice(0, 2).forEach((filter, index) => {
      if (filter.field) {
        queryParams[`filter${index}_field`] = filter.field;
        queryParams[`filter${index}_value`] = filter.value;
      }
    });
  }

  return instance.get(`/processing/batch/${batchId}/results`, {
    params: queryParams
  });
};

/**
 * Download all batch results as a flat array (large page size) for CSV export
 * @param {string} batchId - Batch ID
 * @returns {Promise} Promise containing all results
 */
export const downloadBatchResults = (batchId) => {
  return instance.get(`/processing/batch/${batchId}/results`, {
    params: { page: 0, size: 100000 }
  });
};

/**
 * Get recent batch history
 * @param {number} limit - Number of recent batches to retrieve
 * @param {string} status - Filter by batch status ('ALL', 'PROCESSING', 'COMPLETED', 'FAILED', 'CANCELLED')
 * @returns {Promise} Promise containing batch history
 */
export const getRecentBatches = (limit = 10, status = 'ALL') => {
  console.log('[Service] Fetching recent batches with limit:', limit, 'status:', status);

  return instance.get('/processing/recent', {
    params: { limit, status }
  }).catch(error => {
    console.warn('[Service] Recent batches API not available:', error.message);
    // Return empty data structure on error - batch history is not critical
    return { data: [] };
  });
};

/**
 * Re-process an existing batch
 * @param {string} batchId - Batch ID to re-process
 * @param {string} mode - 'ALL' to re-process every row, 'FAILED_ONLY' to re-process only failed rows
 * @returns {Promise} Promise containing the updated batch status
 */
export const reprocessBatch = (batchId, mode = 'FAILED_ONLY') => {
  console.log('[Service] Re-processing batch:', batchId, 'mode:', mode);
  return instance.post(`/processing/batch/${batchId}/reprocess`, null, {
    params: { mode },
    timeout: 300000, // 5 minutes
  });
};

// ==================== RESULT VALIDATION ====================

/**
 * Validate processing result
 * @param {number} resultId - Result ID
 * @param {string} validatedCategory - Validated category name
 * @returns {Promise} Promise containing validation response
 */
export const validateResult = (resultId, validatedCategory) => {
  return instance.post(`/processing/results/${resultId}/validate`, {
    validatedCategory
  });
};

/**
 * Bulk validate results
 * @param {array} resultIds - Array of result IDs
 * @param {string} validatedCategory - Validated category name for all
 * @returns {Promise} Promise containing bulk validation response
 */
export const bulkValidateResults = (resultIds, validatedCategory) => {
  return instance.post('/processing/results/bulk-validate', {
    resultIds,
    validatedCategory
  });
};

// ==================== EXCEL OPERATIONS ====================

/**
 * Ingest Excel file for categorization
 * @param {File} file - Excel file
 * @returns {Promise} Promise containing ingestion response
 */
export const ingestExcelFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);

  return instance.post('/excel/ingest', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Export categorized data to Excel
 * @param {string} status - Optional status filter (PENDING, CATEGORIZED, VALIDATED)
 * @returns {Promise} Promise containing Excel file blob
 */
export const exportToExcel = (status = '') => {
  const endpoint = status ? `/excel/export/status/${status}` : '/excel/export';

  return instance.get(endpoint, {
    responseType: 'blob',
  });
};

/**
 * Export all data to Excel
 * @returns {Promise} Promise containing Excel file blob
 */
export const exportAllToExcel = () => {
  return instance.get('/excel/export/all', {
    responseType: 'blob',
  });
};

// ==================== DATA ITEMS ====================

/**
 * Get all data items with pagination
 * @param {object} params - {page, size, status}
 * @returns {Promise} Promise containing data items
 */
export const getAllDataItems = (params = {}) => {
  const { page = 0, size = 20, status = '' } = params;

  return instance.get('/data-items', {
    params: { page, size, status }
  });
};

/**
 * Get data item by ID
 * @param {number} dataItemId - Data item ID
 * @returns {Promise} Promise containing data item details
 */
export const getDataItemById = (dataItemId) => {
  return instance.get(`/data-items/${dataItemId}`);
};

/**
 * Categorize single data item
 * @param {object} dataItem - Data item to categorize
 * @returns {Promise} Promise containing categorization result
 */
export const categorizeDataItem = (dataItem) => {
  return instance.post('/data-items/categorize', dataItem);
};

/**
 * Categorize batch of data items
 * @param {array} dataItems - Array of data items to categorize
 * @returns {Promise} Promise containing batch categorization results
 */
export const categorizeBatchDataItems = (dataItems) => {
  return instance.post('/data-items/batch/categorize', { dataItems });
};

/**
 * Assign category to data item
 * @param {number} dataItemId - Data item ID
 * @param {number} categoryId - Category ID
 * @returns {Promise} Promise containing assignment response
 */
export const assignCategoryToDataItem = (dataItemId, categoryId) => {
  return instance.post(`/data-items/${dataItemId}/category/${categoryId}`);
};

/**
 * Recategorize data item
 * @param {number} dataItemId - Data item ID
 * @returns {Promise} Promise containing recategorization result
 */
export const recategorizeDataItem = (dataItemId) => {
  return instance.post(`/data-items/${dataItemId}/recategorize`);
};

/**
 * Teams are dynamically fetched from the backend based on rules
 * @returns {Promise} Promise containing list of teams
 */
export const getTeams = () => {
  return instance.get('/teams');
};

// ==================== TEAM MANAGEMENT (Authorization) ====================

/**
 * Get all teams with full details (id, name, description, members, etc.)
 * @returns {Promise} Promise containing array of TeamDTO
 */
export const getAllTeamsDetailed = () => {
  return instance.get('/teams/all');
};

/**
 * Create a new team. The requesting user becomes the ADMIN.
 * @param {object} teamData - { name, description, memberUserIds[] }
 * @returns {Promise} Promise containing created TeamDTO (201)
 */
export const createTeam = (teamData) => {
  return instance.post('/teams', teamData);
};

/**
 * Get team by ID (includes members list)
 * @param {number} teamId
 * @returns {Promise} Promise containing TeamDTO
 */
export const getTeamById = (teamId) => {
  return instance.get(`/teams/${teamId}`);
};

/**
 * Delete (deactivate) a team. Only ADMINs can do this.
 * @param {number} teamId
 * @returns {Promise}
 */
export const deleteTeam = (teamId) => {
  return instance.delete(`/teams/${teamId}`);
};

/**
 * Add a member to a team. Only ADMINs can add members.
 * @param {number} teamId
 * @param {object} memberData - { userId, role ('ADMIN'|'MEMBER') }
 * @returns {Promise} Promise containing updated TeamDTO
 */
export const addTeamMember = (teamId, memberData) => {
  return instance.post(`/teams/${teamId}/members`, memberData);
};

/**
 * Remove a member from a team. Only ADMINs can remove members.
 * @param {number} teamId
 * @param {string} memberUserId
 * @returns {Promise} Promise containing updated TeamDTO
 */
export const removeTeamMember = (teamId, memberUserId) => {
  return instance.delete(`/teams/${teamId}/members/${memberUserId}`);
};

/**
 * Get all members of a team.
 * @param {number} teamId
 * @returns {Promise} Promise containing array of TeamMemberDTO
 */
export const getTeamMembers = (teamId) => {
  return instance.get(`/teams/${teamId}/members`);
};

/**
 * Get teams the current user belongs to.
 * @returns {Promise} Promise containing array of TeamDTO
 */
export const getMyTeams = () => {
  return instance.get('/teams/my-teams');
};

/**
 * Get teams where the current user is an ADMIN.
 * Used by the rule creation UI to restrict the team dropdown to admin teams only.
 * @returns {Promise} Promise containing array of TeamDTO
 */
export const getMyAdminTeams = () => {
  return instance.get('/teams/my-admin-teams');
};

/**
 * Check if current user has access to a specific team.
 * @param {string} teamName
 * @returns {Promise} Promise containing { hasAccess, isAdmin, teamName, userId }
 */
export const checkTeamAccess = (teamName) => {
  return instance.get('/teams/check-access', { params: { teamName } });
};

// ==================== ATTESTATION ====================

/**
 * Attest (accept or deny) a single categorization result
 * @param {object} request - { resultId, verdict ('ACCEPTED'|'DENIED'), correctedCategory, attestedBy, comments }
 * @returns {Promise} Promise containing attested data
 */
export const attestResult = (request) => {
  return instance.post('/attestation', request);
};

/**
 * Bulk attest multiple results
 * @param {array} requests - Array of attestation requests
 * @returns {Promise} Promise containing summary
 */
export const bulkAttestResults = (requests) => {
  return instance.post('/attestation/bulk', requests);
};

/**
 * Get attested data for a batch
 * @param {string} batchId - Batch ID
 * @param {object} params - { page, size }
 * @returns {Promise}
 */
export const getAttestedDataByBatch = (batchId, params = {}) => {
  const { page = 0, size = 50 } = params;
  return instance.get(`/attestation/batch/${batchId}`, { params: { page, size } });
};

/**
 * Get attestation statistics for a batch
 * @param {string} batchId
 * @returns {Promise}
 */
export const getAttestationStats = (batchId) => {
  return instance.get(`/attestation/stats/${batchId}`);
};

/**
 * Accept ALL categorized results for a batch in one server-side call.
 * Calls the backend bulk-accept endpoint (no need to send IDs).
 * @param {string} batchId - Batch ID
 * @returns {Promise} Promise containing { acceptedCount, batchId, message }
 */
export const acceptAllBatch = (batchId) => {
  return instance.post(`/processing/batch/${batchId}/accept-all`);
};

/**
 * Check if a result has been attested
 * @param {number} resultId
 * @returns {Promise}
 */
export const checkAttestation = (resultId) => {
  return instance.get(`/attestation/check/${resultId}`);
};

/**
 * Get all attested data for LLM training export
 * @param {object} params - { page, size }
 * @returns {Promise}
 */
export const getTrainingData = (params = {}) => {
  const { page = 0, size = 100 } = params;
  return instance.get('/attestation/training-data', { params: { page, size } });
};

const dataCategorizationServiceExport = {
  // Categories
  getAllIncidentCategories,
  getIncidentCategoryById,
  createIncidentCategory,
  updateIncidentCategory,
  deleteIncidentCategory,
  changeCategoryStatus,
  bulkDeleteCategories,
  searchCategories,
  getCategoryAuditLog,
  validateCategoryData,
  // Rules
  getAllRules,
  getRuleById,
  createRule,
  updateRule,
  deleteRule,
  toggleRuleStatus,
  // Batch Processing
  uploadBatchFile,
  getBatchStatus,
  cancelBatch,
  getBatchResults,
  getRecentBatches,
  // Result Validation
  validateResult,
  bulkValidateResults,
  // Attestation
  attestResult,
  bulkAttestResults,
  acceptAllBatch,
  getAttestedDataByBatch,
  getAttestationStats,
  checkAttestation,
  getTrainingData,
  // Excel Operations
  ingestExcelFile,
  exportToExcel,
  exportAllToExcel,
  // Data Items
  getAllDataItems,
  getDataItemById,
  categorizeDataItem,
  categorizeBatchDataItems,
  assignCategoryToDataItem,
  recategorizeDataItem,
  // Teams
  getTeams,
  // Team Management (Authorization)
  getAllTeamsDetailed,
  createTeam,
  getTeamById,
  deleteTeam,
  addTeamMember,
  removeTeamMember,
  getTeamMembers,
  getMyTeams,
  getMyAdminTeams,
  checkTeamAccess,
};

export default dataCategorizationServiceExport;
